import * as z from "zod";

import { CalendarSelector, DropdownSelect } from "components/form-control";
import {
  CreateDecreeInput,
  CreatePermitDecreeInput,
  CreateTemporaryDecreeInput,
  PermitDecreeType,
  TemporaryDecreeType,
} from "types/decree.type";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { permitDecreeTypes, temporaryDecreeTypes } from "@/modules/restriction/restriction.const";
import { useContext, useEffect, useMemo } from "react";

import { Button } from "@/components/ui/button";
import { CityContext } from "@/contexts/city-context-provider";
import { DecreeCategory } from "@/types/gql/graphql";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { decreeCategoryType } from "./decree.const";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

type CreateDecreeProps = {
  closeModal: () => void;
  saveDecreeFile: (input: CreateDecreeInput) => void;
};

const FILE_SIZE_LIMIT_MB = 10;
const FILE_SIZE_LIMIT_BYTES = FILE_SIZE_LIMIT_MB * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const baseDecreeSchema = z.object({
  cityId: z.string().min(1, "La ville est requise"),
  name: z.string().min(1, "Le nom est requis"),
  attachedFile: z
    .instanceof(File, { message: "Le fichier de l'arrêté est requis" })
    .refine((file) => ALLOWED_FILE_TYPES.includes(file.type), {
      message: "Seuls les fichiers PDF, DOC et DOCX sont autorisés.",
    })
    .refine((file) => file.size <= FILE_SIZE_LIMIT_BYTES, {
      message: `Limite de taille de fichier : ${FILE_SIZE_LIMIT_MB}MB.`,
    }),
  comments: z.string().optional(),
  type: z.string(),
  publishedDate: z.union([z.date(), z.string()]),
});

const nonPermitCategories = [DecreeCategory.Permanent, DecreeCategory.NotRelated, DecreeCategory.Unknown] as const;

const decreeSchema = z.discriminatedUnion("category", [
  baseDecreeSchema.extend({
    category: z.literal(DecreeCategory.Permit),
    type: z.string().min(1, "Le type est requis"),
    startDate: z.union([z.date(), z.string()]),
    endDate: z.union([z.date(), z.string()]),
  }),
  baseDecreeSchema.extend({
    category: z.literal(DecreeCategory.Temporary),
    type: z.string().min(1, "Le type est requis"),
  }),
  baseDecreeSchema.extend({
    category: z.enum(nonPermitCategories),
  }),
]);

type DecreeFormValues = z.infer<typeof decreeSchema>;

export const CreateDecreeModal = ({ closeModal, saveDecreeFile }: CreateDecreeProps) => {
  const { currentCity } = useContext(CityContext);
  const form = useForm<DecreeFormValues>({
    resolver: zodResolver(decreeSchema),
    defaultValues: {
      name: "",
      cityId: currentCity?.id ?? "",
      category: DecreeCategory.Temporary,
      comments: "",
      type: temporaryDecreeTypes[0].value,
      publishedDate: new Date(),
    },
    mode: "all",
  });

  const { handleSubmit, control, watch, setValue } = form;

  const saveDecree = (values: DecreeFormValues) => {
    const commonValues: CreateDecreeInput = {
      cityId: values.cityId,
      name: values.name,
      comments: values.comments,
      attachedFile: values.attachedFile,
      category: values.category as DecreeCategory,
      publishedDate: new Date(values.publishedDate),
    };

    switch (values.category) {
      case DecreeCategory.Temporary: {
        const newDecree: CreateTemporaryDecreeInput = {
          ...commonValues,
          type: values.type as TemporaryDecreeType,
        };
        saveDecreeFile(newDecree);
        break;
      }
      case DecreeCategory.Permit: {
        const newDecree: CreatePermitDecreeInput = {
          ...commonValues,
          startDate: new Date(values.startDate),
          endDate: new Date(values.endDate),
          type: values.type as PermitDecreeType,
        };
        saveDecreeFile(newDecree);
        break;
      }
      default: {
        saveDecreeFile(commonValues);
        break;
      }
    }

    closeModal();
  };

  const selectedCategory = watch("category");

  const { showTypeDropdown, typeList } = useMemo(() => {
    let typeList: { value: string; label: string }[] = [];
    const showTypeDropdown =
      selectedCategory === DecreeCategory.Temporary || selectedCategory === DecreeCategory.Permit;

    switch (selectedCategory) {
      case DecreeCategory.Temporary:
        typeList = temporaryDecreeTypes;
        break;
      case DecreeCategory.Permit:
        typeList = permitDecreeTypes;
        break;
    }

    return { showTypeDropdown, typeList };
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedCategory === DecreeCategory.Temporary) {
      setValue("type", temporaryDecreeTypes[0].value);
    } else if (selectedCategory === DecreeCategory.Permit) {
      setValue("type", permitDecreeTypes[0].value);
    }
  }, [selectedCategory, setValue]);

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a decree</DialogTitle>
          <DialogDescription>Decree information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit(saveDecree)} className="space-y-6">
            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Nom<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="attachedFile"
              render={({ field: { onChange, onBlur, name, ref } }) => (
                <FormItem>
                  <FormLabel>
                    Decree file<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="file"
                      name={name}
                      ref={ref}
                      onBlur={onBlur}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          onChange(file);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="category"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>
                    Category<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <DropdownSelect value={value} options={decreeCategoryType} onValueChange={onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {showTypeDropdown && (
              <FormField
                control={control}
                name="type"
                render={({ field: { onChange, value } }) => (
                  <FormItem>
                    <FormLabel>
                      Type<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <DropdownSelect value={value} options={typeList} onValueChange={onChange} className="w-full" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {selectedCategory === DecreeCategory.Permit && (
              <>
                <FormField
                  control={control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Start Date<span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <CalendarSelector selectedDate={field.value} onChangeValue={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        End Date<span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <CalendarSelector selectedDate={field.value} onChangeValue={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={control}
              name="comments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comment</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Type your message here." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="publishedDate"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Published Date</FormLabel>
                  <FormControl>
                    <CalendarSelector selectedDate={value ?? undefined} onChangeValue={onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="sm:justify-between">
              <Button onClick={closeModal} variant="secondary">
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
